import React from 'react';
import { useMutation, useQuery, NetworkStatus } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMachine } from '@xstate/react';
import { format } from 'date-fns';
import {
    ListCorrespondencesAndEventsDocument,
    CreateCorrespondenceAndDocumentsDocument,
    UpdateCorrespondenceAndDocumentsDocument,
    ListCorrespondenceDocumentDocument,
    CreateCorrespondenceDocument,
    UpdateCorrespondenceDocument,
    DeleteCorrespondenceDocument,
    DeleteTaskFunctionDocument,
    CreateTaskFunctionDocument,
    UpdateEventDocument,
    DeleteEventDocument,
    CreateTaskDocument,
    ListTasksDocument,
    SortOrder,
    ListDocumentsDocument,
    GetProjectDocument,
    type Projekt,
} from '~/gql/ucpw/graphql';
import { useContextAndRefetchQueries } from '~/hooks/useContextAndRefetchQueries';
import { join, withRenderers, mutationGuard } from '~/utils';
import { useModal, useDeleteWithConfirmation, usePermission, useViewer } from '~/hooks';
import { tables } from '~/pages/projects/meta/data/correspondence.schema';
import { useServices } from '~/pages/projects/ui/Calendar/hooks/useServices';
import { eventsMachine } from '../ui/Calendar/eventsMachine';
import { resolveProjectDetails } from '~/pages/projects/ui/Calendar/hooks/useEventProjectDetails';
import { usePdfRefetchQueries } from './usePdf';
import { isLockedCorrespondenceRow } from '~/pages/projects/meta/data/lockedCorrespondence';
import { client } from '~/apollo';
import { pxConnectVersandFromMetadata } from '~/meta/data/px-connect';

export const useCorrespondence = (
    props: {
        subprojectId?: number;
        /** Übersichtsmodus: Korrespondenz aller Subprojekte, eigene Spalten. */
        projectId?: number;
        /** Nur für den Bearbeitungsstand der PX-Schadenberichte (Projekt-Metadaten). */
        pxProjectId?: number;
    } = {}
) => {
    const navigate = useNavigate();
    const location = useLocation();
    const viewer = useViewer();
    const { subprojectId, projectId } = props;
    const employeeId = viewer?.app?.mitarbeiterId;

    const { coverSheet: coverSheetRefetchQueries } = usePdfRefetchQueries({ subprojectId });
    const { refetchQueries: listDocumentsDocumentRefetchQueries } = useContextAndRefetchQueries(
        ListDocumentsDocument,
        { filter: { subprojektId: subprojectId }, limit: 100 }
    );

    // ===============================================
    //  LISTING
    // ===============================================

    const variables = {
        ...(projectId && { projektId: projectId }),
        ...(subprojectId && { subprojektId: subprojectId }),
        limit: 500,
    };

    const contextAndRefetchQueries = useContextAndRefetchQueries(
        ListCorrespondencesAndEventsDocument,
        variables,
        [coverSheetRefetchQueries, ...listDocumentsDocumentRefetchQueries]
    );

    const {
        data,
        loading: listCorrespondencesAndEventsLoading,
        networkStatus,
    } = useQuery(ListCorrespondencesAndEventsDocument, {
        variables,
        notifyOnNetworkStatusChange: true,
        context: contextAndRefetchQueries.context,
    });
    // Bearbeitungsstand versendeter PX-Schadenberichte (UCPWO-410) liegt in den
    // Projekt-Metadaten, nicht an der Korrespondenz. Die Projektseite hat das Projekt
    // bereits geladen, die Abfrage kommt also aus dem Cache.
    const metadataProjectId = props.pxProjectId ?? projectId;
    const { data: projectData } = useQuery(GetProjectDocument, {
        variables: { id: metadataProjectId as number },
        context: { clientName: 'ucpw' },
        skip: !metadataProjectId,
    });
    const pxVersand = React.useMemo(
        () => pxConnectVersandFromMetadata((projectData?.item?.item as Projekt | undefined)?.metadata),
        [projectData]
    );
    const correspondencesAndEvents = React.useMemo(() => {
        const items = data?.app?.items?.items || [];
        if (!Object.keys(pxVersand).length) return items;
        return items.map((item: any) => {
            const stand = item?.korrespondenzId && pxVersand[String(item.korrespondenzId)];
            return stand ? { ...item, pxBearbeitungsstand: stand } : item;
        });
    }, [data, pxVersand]);
    const services = useServices({ refetchQueries: contextAndRefetchQueries.refetchQueries });
    const [states, send] = useMachine(eventsMachine, { services });

    // ===============================================
    // (C)R(UD) Correspondence
    // ===============================================

    const [createMutation, { loading: createLoading }] = useMutation(
        CreateCorrespondenceDocument,
        contextAndRefetchQueries
    );
    const [
        createCorrespondenceAndDocumentsMutation,
        { loading: createCorrespondenceAndDocumentLoading },
    ] = useMutation(CreateCorrespondenceAndDocumentsDocument, contextAndRefetchQueries);
    const [
        updateCorrespondenceAndDocumentsMutation,
        { loading: updateCorrespondenceAndDocumentsLoading },
    ] = useMutation(UpdateCorrespondenceAndDocumentsDocument, contextAndRefetchQueries);

    const [updateMutation, { loading: updateLoading }] = useMutation(
        UpdateCorrespondenceDocument,
        contextAndRefetchQueries
    );
    const [deleteMutation, { loading: deleteLoading }] = useMutation(
        DeleteCorrespondenceDocument,
        contextAndRefetchQueries
    );
    const { deleteWithConfirmation: deleteCorrespondence } = useDeleteWithConfirmation({
        deleteMutation: async (variables: any) =>
            await mutationGuard(deleteMutation, {
                variables,
                path: 'data.item.error',
                actions: ['addBreadcrumb', 'captureMessage'],
            }),
        refetchQueries: contextAndRefetchQueries.refetchQueries,
        dependencies: [deleteMutation, contextAndRefetchQueries.refetchQueries],
    });

    // ===============================================
    // (C)R(U)D Event
    // ===============================================

    const [updateEventMutation, { loading: updateEventLoading }] = useMutation(
        UpdateEventDocument,
        contextAndRefetchQueries
    );
    const [deleteEventMutation, { loading: deleteEventLoading }] = useMutation(
        DeleteEventDocument,
        contextAndRefetchQueries
    );
    const { deleteWithConfirmation: deleteEvent } = useDeleteWithConfirmation({
        deleteMutation: async (variables: any) =>
            await mutationGuard(deleteEventMutation, {
                variables,
                path: 'data.item.error',
                actions: ['addBreadcrumb', 'captureMessage'],
            }),
        refetchQueries: contextAndRefetchQueries.refetchQueries,
        dependencies: [deleteEventMutation, contextAndRefetchQueries.refetchQueries],
    });

    // ===============================================
    // (C)RU(D) TaskFunction
    // ===============================================

    const listTasksContextAndRefetchQueries = useContextAndRefetchQueries(
        ListTasksDocument,
        {
            limit: 500,
            orderBy: [{ angelegt: SortOrder.Desc }],
            filter: { subprojektId: subprojectId },
        },
        [coverSheetRefetchQueries]
    );

    const options = {
        context: { clientName: 'ucpw' },
        refetchQueries: [
            ...contextAndRefetchQueries.refetchQueries,
            ...listTasksContextAndRefetchQueries.refetchQueries,
        ],
    };

    const [createTaskFunction, { loading: createTaskFunctionLoading }] = useMutation(
        CreateTaskFunctionDocument,
        options
    );

    const [deleteTaskFunction, { loading: deleteTaskFunctionLoading }] = useMutation(
        DeleteTaskFunctionDocument,
        options
    );

    // ===============================================
    // (C)RUD Task
    // ===============================================

    const [createTaskMutation, { loading: createTaskLoading }] = useMutation(
        CreateTaskDocument,
        options
    );

    const deleteWithConfirmation = (row: any) => {
        const original = row?.original || {};
        console.log('deleteWithConfirmation', { original });
        const cond = original?.terminId;
        const variables = cond
            ? { id: original?.terminId, version: original?.termin?.version, forceOverwrite: true }
            : {
                  id: original?.korrespondenzId,
                  version: original?.korrespondenz?.version,
                  forceOverwrite: true,
              };

        const deleteMutation = cond ? deleteEvent : deleteCorrespondence;
        return deleteMutation(variables);
    };

    const loading =
        listCorrespondencesAndEventsLoading ||
        createLoading ||
        updateLoading ||
        deleteLoading ||
        updateEventLoading ||
        deleteEventLoading ||
        createTaskLoading ||
        deleteTaskFunctionLoading ||
        createTaskFunctionLoading ||
        createCorrespondenceAndDocumentLoading ||
        updateCorrespondenceAndDocumentsLoading ||
        networkStatus === NetworkStatus.loading;

    // ===============================================
    // MODAL
    // ===============================================

    const { onOpen, dispatch } = useModal();

    // ===============================================
    // TABLE
    // ===============================================

    const getCorrespondenceDocument = async (korrespondenzId: number) => {
        const { data } = await client.query({
            query: ListCorrespondenceDocumentDocument,
            variables: {
                limit: 200,
                filter: { korrespondenzId },
            },
            context: { clientName: 'ucpw' },
        });
        return data?.items?.items ?? [];
    };

    const setCorrespondenceModal = React.useCallback(
        (props: Record<string, any> = {}) =>
            dispatch?.({
                type: 'setModal',
                data: {
                    modal: 'CorrespondenceModal',
                    props: {
                        createMutation,
                        createCorrespondenceAndDocumentsMutation,
                        updateCorrespondenceAndDocumentsMutation,
                        updateMutation: async (variables: any) =>
                            await mutationGuard(updateMutation, {
                                variables,
                                path: 'data.item.error',
                                actions: ['addBreadcrumb', 'captureMessage'],
                            }),
                        loading,
                        subprojectId,
                        createTaskMutation,
                        createTaskFunction,
                        deleteTaskFunction,
                        ...props,
                    },
                },
            }),
        [props, loading]
    );


    const onClick = React.useCallback(
        async (row: any) => {
            const { terminId, termin: event, korrespondenz: correspondence } = row?.original;
            if (terminId && event) {
                console.log('useCorr.edit.event', { event });
                dispatch?.({
                    type: 'setModal',
                    data: {
                        modal: 'CreateEvent',
                        props: {
                            onCreateEvent: (variables: any) =>
                                send({ type: 'CREATE_EVENT', variables }),
                            onUpdateEvent: (variables: any) =>
                                send({ type: 'UPDATE_EVENT', variables }),
                        },
                    },
                });
                dispatch?.({
                    type: 'formState',
                    data: {
                        title: `Termin bearbeiten`,
                        variables,
                        version: event?.version,
                        id: event.id,
                        employeeId: {
                            value: event?.mitarbeiterIdSachbearbeiter,
                            label: join([
                                event?.sachbearbeiter?.name,
                                event?.sachbearbeiter?.vorname,
                            ]),
                        },
                        planed: format(new Date(event?.geplant), 'yyyy-MM-dd'),
                        time: format(new Date(event?.geplant), 'HH:mm'),
                        typeSnippetId: { value: event?.typSnippetId },
                        duration: event?.dauer,
                        comment: event?.bemerkung,
                        done: Boolean(event?.erledigt),
                        confirmed: event?.confirmed,
                        prio: event?.prio > 0,
                        refetchQueries: contextAndRefetchQueries?.refetchQueries,
                        eventSubprojectResidentialUnits: event?.terminSubprojektWohneinheit || [],
                        updateEventMutation: async (variables: any) =>
                            await mutationGuard(updateEventMutation, {
                                variables,
                                path: 'data.item.error',
                                actions: ['addBreadcrumb', 'captureMessage'],
                            }),
                        ...resolveProjectDetails(row?.original?.termin?.subprojekt),
                    },
                });
                return onOpen?.();
            }
            const correspondenceDocument = await getCorrespondenceDocument(correspondence?.id);
            const isLocked = isLockedCorrespondenceRow(row);
            setCorrespondenceModal({ isLocked });
            dispatch?.({
                type: 'setRow',
                data: {
                    row: {
                        original: {
                            ...correspondence,
                            korrespondenzDokument: correspondenceDocument,
                        },
                    },
                },
            });
            onOpen?.();
        },
        [dispatch, onOpen, setCorrespondenceModal, getCorrespondenceDocument]
    );

    React.useEffect(() => {
        if (location?.state?.__typename === 'Dokument') {
            const correspondenceDocument = location?.state?.korrespondenzDokument?.[0];
            if (correspondenceDocument?.korrespondenzId) {
                const correspondence = correspondencesAndEvents.find(
                    (item) => item?.korrespondenzId === correspondenceDocument?.korrespondenzId
                );
                console.log('useCorrespondence.location.state', location.state, correspondence);
                if (correspondence?.korrespondenz) {
                    const isLocked = isLockedCorrespondenceRow({ original: correspondence });
                    setCorrespondenceModal({ isLocked });

                    // Fetch correspondence documents using getCorrespondenceDocument
                    getCorrespondenceDocument(correspondence.korrespondenz.id).then(
                        (correspondenceDocuments) => {
                            dispatch?.({
                                type: 'setRow',
                                data: {
                                    row: {
                                        original: {
                                            ...correspondence.korrespondenz,
                                            korrespondenzDokument: correspondenceDocuments,
                                        },
                                    },
                                },
                            });
                            onOpen?.();
                        }
                    );
                }
            } else {
                setCorrespondenceModal();
                dispatch?.({
                    type: 'setRow',
                    data: { row: { original: location.state } },
                });
                onOpen?.();
            }
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [
        location,
        navigate,
        setCorrespondenceModal,
        dispatch,
        onOpen,
        correspondencesAndEvents,
        getCorrespondenceDocument,
    ]);

    /** Permin */
    const hasEditAllCorrespondence = viewer.hasPermission(
        'additional.editAllCorrespondence',
        'enabled'
    );
    const {
        canView: canViewCorr,
        canEdit: canEditCorr,
        canDelete: canDeleteCorr,
    } = usePermission('project.correspondence');
    const readOnlyCorr = canViewCorr && !canEditCorr;

    const {
        canEdit: canEditEvent,
        canDelete: canDeleteEvent,
        canView: canViewEvent,
    } = usePermission('project.schedule');

    const is = (row: any) => ({
        correspondence: Boolean(row?.original?.korrespondenzId),
        event: Boolean(row?.original?.terminId),
    });

    const isCreator = (row: any) => {
        const creatorId = row?.original?.mitarbeiterIdUrheber;
        return creatorId === null ? true : creatorId === employeeId;
    };

    const controls = [
        /** <CORRESPONDENCE> */
        projectId && {
            title: 'Zum SubProjekt',
            props: {
                onClick: ({ original }: any) => {
                    const subprojekt = original?.korrespondenz?.subprojekt;
                    subprojekt &&
                        navigate(`/projekte/${subprojekt?.projekt?.id}/${subprojekt?.id}/details`);
                },
            },
            enabled: (row: any) => is(row).correspondence,
        },
        {
            title: 'Ansehen',
            props: { onClick },
            enabled: (row: any) => {
                const isCorrespondence = is(row).correspondence;
                const isLocked = isLockedCorrespondenceRow(row);

                // Schnittstellen-Nachrichten (GDV / PX Connect) sind immer ansehbar
                if (isCorrespondence && isLocked) {
                    return true;
                }

                if (isCorrespondence && hasEditAllCorrespondence) {
                    return false;
                }
                return isCorrespondence && (readOnlyCorr || (canEditCorr && !isCreator(row)));
            },
        },
        {
            title: 'Bearbeiten',
            props: { onClick },
            enabled: (row: any) => {
                const isCorrespondence = is(row).correspondence;
                const isLocked = isLockedCorrespondenceRow(row);

                // … aber nicht bearbeitbar
                if (isCorrespondence && isLocked) {
                    return false;
                }

                if (isCorrespondence && hasEditAllCorrespondence) {
                    return true;
                }
                return isCorrespondence && !readOnlyCorr && canEditCorr && isCreator(row);
            },
        },
        {
            title: 'divider',
            enabled: (row: any) => {
                const isCorrespondence = is(row).correspondence;
                const isLocked = isLockedCorrespondenceRow(row);

                // Kein Trenner, wenn darunter nichts kommt (nicht löschbar)
                if (isCorrespondence && isLocked) {
                    return false;
                }

                if (isCorrespondence && hasEditAllCorrespondence) {
                    return true;
                }
                return (
                    isCorrespondence &&
                    canDeleteCorr &&
                    isCreator(row) &&
                    (canViewCorr || canEditCorr)
                );
            },
        },
        {
            title: 'Löschen',
            props: {
                color: 'red.400',
                onClick: deleteWithConfirmation,
            },
            enabled: (row: any) => {
                const isCorrespondence = is(row).correspondence;
                const isLocked = isLockedCorrespondenceRow(row);

                // … und nicht löschbar
                if (isCorrespondence && isLocked) {
                    return false;
                }

                if (isCorrespondence && hasEditAllCorrespondence) {
                    return true;
                }
                return isCorrespondence && canDeleteCorr && isCreator(row);
            },
        },
        /** </CORRESPONDENCE> */

        /** <EVENT> */
        {
            title: 'Ansehen',
            props: { onClick },
            enabled: (row: any) => row?.original?.terminId && canViewEvent && !canEditEvent,
        },
        {
            title: 'Bearbeiten',
            props: { onClick },
            enabled: (row: any) => row?.original?.terminId && canEditEvent,
        },
        {
            title: 'divider',
            enabled: (row: any) => row?.original?.terminId && canEditEvent && canDeleteEvent,
        },
        {
            title: 'Löschen',
            props: {
                color: 'red.400',
                onClick: deleteWithConfirmation,
            },
            enabled: (row: any) => row?.original?.terminId && canDeleteEvent,
        },
        /** </EVENT> */
    ].filter(Boolean);

    const columns = React.useMemo(() => {
        const cols = projectId
            ? tables?.projectOverviewCorrespondence.columns
            : tables?.correspondence.columns;

        return withRenderers(cols, controls);
    }, [controls, projectId]);
    const hiddenColumns = tables.correspondence.hiddenColumns;

    const onCreate = () => {
        setCorrespondenceModal();
        dispatch?.({ type: 'setRow', data: { row: { original: {} } } });
        onOpen?.();
    };

    // Opens the Korrespondenz modal prefilled with an externally prepared draft
    // (e.g. the PX Connect Schadenbericht flow). Goes through the same
    // setCorrespondenceModal path as onCreate so the modal receives the create/
    // update mutation functions it needs — opening it without them fails with
    // "createCorrespondenceAndDocumentsMutation is not a function".
    const openDraft = React.useCallback(
        (row: Record<string, any>) => {
            setCorrespondenceModal();
            dispatch?.({ type: 'setRow', data: { row: { original: row } } });
            onOpen?.();
        },
        [setCorrespondenceModal, dispatch, onOpen]
    );

    return {
        data: !canViewEvent
            ? correspondencesAndEvents.filter((item) => !Boolean(item?.terminId))
            : correspondencesAndEvents,
        loading,
        columns,
        hiddenColumns,
        pageSize: 500,
        onClick,
        onCreate,
        openDraft,
    };
};
